import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Button,
  Checkbox,
  Indicators,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Input,
  Textarea,
  AttachmentList,
  FilePreview,
  FileUploadStatus,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from 'ui'
import CreatableSelect from 'react-select/creatable'
import { useCurrentUser } from '../../../queries/useCurrentUser'
import {
  useFileUploadWorkflow,
  useTempFileUploadWorkflow,
  useTempAttachmentAnalysis,
  mapApiResponseToFormFields,
  useCreateEventWithFiles,
  useDeleteAttachment,
} from '../../../queries'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { useCreateEventMutation } from '../../../queries/useCreateEventMutation'
import { useUpdateEventMutation } from '../../../queries/useUpdateEventMutation'
import { toast } from 'react-hot-toast'
import { useFetchWithAuth } from '../../../session/useFetchWithAuth'
import { getEngagementTooltip } from '../../../components/EngagementTooltips'
import { AccessLevel } from '../../../queries/types'

// Validation schema
const schema = yup.object({
  name: yup.string().required('Cannot be blank'),
  open: yup.boolean().optional(),
  invite_all_educators: yup.boolean().optional(),
  invite_all_professionals: yup.boolean().optional(),
  expectation_statement: yup.string().required('Cannot be blank'),
  accomplish_statement: yup.string().required('Cannot be blank'),
  grade_levels: yup.array().of(yup.string().required()).optional(),
  secondaryLanguages: yup.array().of(yup.string().required()).optional(),
  engagement_opportunities: yup.array().of(yup.string().required()).optional(),
  attachments: yup
    .array()
    .of(
      yup.object({
        id: yup.string().required(),
        name: yup.string().nullable(),
        downloadUrl: yup.string().nullable(),
        position: yup.number().nullable(),
        status: yup.string().required(),
        uploadURL: yup.string().nullable(),
      })
    )
    .optional(),
})
export type EventStepOne = yup.InferType<typeof schema>

interface CreateEventStepOneFormProps {
  existingEvent?: any // Using any for now, could be typed properly with GraphQL types
}

export const CreateEventStepOneForm: React.FC<CreateEventStepOneFormProps> = ({
  existingEvent,
}) => {
  const { data: user, isLoading } = useCurrentUser()
  const isEducator = user?.userRole?.toLowerCase() === 'educator'
  const canSelectOpenAudience = user?.accessLevel === AccessLevel.Admin
  const navigate = useNavigate()
  const createEventMutation = useCreateEventMutation()
  const updateEventMutation = useUpdateEventMutation()
  const deleteAttachmentMutation = useDeleteAttachment()
  const isEditMode = !!existingEvent
  const existingEventHasAudience =
    Boolean(existingEvent?.invite_all_educators) ||
    Boolean(existingEvent?.invite_all_professionals)
  const existingEventCreatorRole = existingEvent?.organizer?.role?.toLowerCase()
  const defaultInviteAllEducators =
    Boolean(existingEvent?.invite_all_educators) ||
    (Boolean(existingEvent?.open) && !existingEventHasAudience)
  const defaultInviteAllProfessionals =
    Boolean(existingEvent?.invite_all_professionals) ||
    (Boolean(existingEvent?.open) &&
      !existingEventHasAudience &&
      existingEventCreatorRole !== 'professional')

  // Debug: Log the existing event attachments
  useEffect(() => {
    if (existingEvent) {
      console.log('Existing event:', existingEvent)
      console.log('Existing event attachments:', existingEvent.attachments)
    }
  }, [existingEvent])

  // Temporary file upload hooks (for when we don't have an event ID yet)
  const {
    tempFiles,
    uploadTempFiles,
    createEventWithTempFiles,
    progress,
    isUploading,
    isError: isUploadError,
    error: uploadError,
    convertTempFilesToPreviews,
    deleteTempFile,
    getSignedUrls,
  } = useTempFileUploadWorkflow()

  // Direct event creation mutation (for creating from analysis)
  const createEventMutationWithFiles = useCreateEventWithFiles()

  // Use temporary files as initial attachments if available
  const [initialAttachmentsSet, setInitialAttachmentsSet] = useState(false)

  // Reset temp file IDs when component mounts or when starting a new event
  useEffect(() => {
    if (!isEditMode && !existingEvent) {
      // Clear any previous temp file IDs when starting a fresh create form
      setNewTempFileIds([])
      setInitialAttachmentsSet(false)
    }
  }, [isEditMode, existingEvent])

  // Track newly uploaded temp file IDs during this session
  const [newTempFileIds, setNewTempFileIds] = useState<string[]>([])

  // State for storing analyzed data
  const [analyzedData, setAnalyzedData] = useState<any>(null)

  // Attachment analysis hook
  const tempAnalysisMutation = useTempAttachmentAnalysis({
    onSuccess: (data) => {
      if (data.success) {
        // Map API response to form fields
        const formValues = mapApiResponseToFormFields(data.event_details)

        // Check if the API created an event directly (when createEvent=true)
        if (data.created_event) {
          // Update toast
          toast.success('Event created from document! Redirecting...', {
            id: 'analyze-toast',
          })

          // Navigate to edit the created event
          navigate(`/app/events/${data.created_event.id}/edit`)
        } else {
          // Store the analyzed data for potential quick event creation
          setAnalyzedData({
            formValues,
            attachmentId: data.source.attachment_id,
          })

          // Update form with extracted data
          form.setValue('name', formValues.name, { shouldValidate: true })
          form.setValue(
            'accomplish_statement',
            formValues.accomplish_statement,
            { shouldValidate: true }
          )
          form.setValue(
            'expectation_statement',
            formValues.expectation_statement,
            { shouldValidate: true }
          )
          form.setValue('grade_levels', formValues.grade_levels, {
            shouldValidate: true,
          })
          form.setValue(
            'engagement_opportunities',
            formValues.engagement_opportunities,
            { shouldValidate: true }
          )

          // Update toast
          toast.success('Form filled with document details!', {
            id: 'analyze-toast',
          })

          // Ask user if they want to create the event immediately
          setTimeout(() => {
            if (
              window.confirm(
                'Would you like to create this event now and continue to the next step?'
              )
            ) {
              handleCreateFromAnalysis(formValues, data.source.attachment_id)
            }
          }, 500) // Small delay to allow the toast to be seen
        }
      } else {
        toast.error('Could not analyze document', { id: 'analyze-toast' })
      }
      setIsAnalyzing(false)
    },
    onError: (error) => {
      console.error('Error analyzing document:', error)
      toast.error(`Error analyzing document: ${error.message}`, {
        id: 'analyze-toast',
      })
      setIsAnalyzing(false)
    },
  })

  // Function to create an event directly from analysis results
  const handleCreateFromAnalysis = async (
    formValues: any,
    attachmentId: string
  ) => {
    try {
      const createToastId = toast.loading('Creating event...')

      // Prepare event data
      const eventData = {
        name: formValues.name,
        open: false, // Default value
        accomplish_statement: formValues.accomplish_statement,
        expectation_statement: formValues.expectation_statement,
        grade_levels: formValues.grade_levels || [],
        engagement_opportunities: formValues.engagement_opportunities || [],
        content_areas: formValues.content_areas || [],
        created_by: user?.userId,
        organized_by: user?.userId,
        status: 'DRAFT',
      }

      // Create the event with the temporary file using the updated mutation
      // The attachmentId is now used in an array format
      const response = await createEventMutationWithFiles.mutateAsync({
        eventData,
        tempFileIds: Array.isArray(attachmentId)
          ? attachmentId
          : [attachmentId],
      })

      toast.success('Event created! Redirecting...', { id: createToastId })

      // Redirect to edit the event (step one) if the event was created successfully
      if (response && response.id) {
        navigate(`/app/events/${response.id}/edit`)
      } else {
        // Fallback to step two if the response format is different
        navigate(`/app/events/${response.id}/two`)
      }
    } catch (error) {
      console.error('Error creating event from analysis:', error)
      toast.error('Failed to create event from analysis')
    }
  }

  // State for tracking analysis in progress
  const [isAnalyzing, setIsAnalyzing] = useState(false)

  useEffect(() => {
    // Only set initial attachments once, and only if we have temp files
    // But skip if we're in edit mode (to avoid overriding existing event attachments)
    // Also only do this if we haven't already reset for a fresh form
    if (
      !isEditMode &&
      !initialAttachmentsSet &&
      tempFiles.length > 0 &&
      newTempFileIds.length === 0
    ) {
      // Only populate temp files if they're from the current session
      // This prevents old temp files from appearing in new create forms
      console.log('Checking if temp files should be pre-populated:', {
        tempFiles: tempFiles.length,
        newTempFileIds: newTempFileIds.length,
        initialAttachmentsSet,
      })

      // Don't auto-populate temp files unless they're explicitly from this session
      // form.setValue('attachments', convertTempFilesToPreviews(), {
      //   shouldValidate: true,
      // })
      // setInitialAttachmentsSet(true)
    }
  }, [tempFiles, initialAttachmentsSet, isEditMode, newTempFileIds])

  // Get fetchWithAuth hook for authenticated requests (used for analysis API calls)
  const fetchWithAuth = useFetchWithAuth()

  // Handle file upload errors
  const handleFileError = (error: Error) => {
    toast.error(error.message || 'Error uploading file')
    console.error('File upload error:', error)
  }

  // Handle file upload completion
  const handleFileUpload = (file: FilePreview) => {
    console.log('🎉 handleFileUpload called with file:', file)

    // Track this as a newly uploaded temp file using serverId (preferred) or id (fallback)
    const fileId = file.serverId || file.id
    if (fileId && !newTempFileIds.includes(fileId)) {
      setNewTempFileIds((prev) => [...prev, fileId])
    }

    // Update the form state to reflect the completed upload
    const currentAttachments = form.getValues('attachments') || []
    console.log('Current form attachments before update:', currentAttachments)

    const updatedAttachments = currentAttachments.map((attachment) => {
      if (attachment.id === file.id) {
        console.log(
          `Updating attachment ${file.id} status from ${attachment.status} to ${file.status}`
        )
        return {
          ...attachment,
          ...file, // Merge in the completed upload data
          name: file.name || attachment.name, // Preserve name if file.name is undefined
        }
      }
      return attachment
    })

    console.log('Updated form attachments after upload:', updatedAttachments)
    form.setValue('attachments', updatedAttachments, { shouldValidate: true })

    toast.success(`${file.name || 'File'} uploaded successfully`)
    console.log('File uploaded - Full details:', {
      id: file.id,
      serverId: file.serverId, // Log server ID
      name: file.name,
      status: file.status,
      downloadUrl: file.downloadUrl,
      hasFile: !!file.file,
      size: file.size,
      position: file.position,
    })
    console.log('Current newTempFileIds after upload:', newTempFileIds)
  }

  // Handle file deletion
  const handleFileDelete = async (file: FilePreview) => {
    try {
      // If the file has an ID and is in Available status, delete it from the server
      if (file.id && file.status === FileUploadStatus.Available) {
        // Show loading toast
        const deleteToastId = toast.loading(
          `Deleting ${file.name || 'file'}...`
        )

        // Check if this is a newly uploaded temp file (regardless of edit mode)
        const fileId = file.serverId || file.id
        if (fileId && newTempFileIds.includes(fileId)) {
          // This is a newly uploaded temp file - delete as temp file
          await deleteTempFile(fileId)
        } else if (isEditMode && existingEvent) {
          // This is an existing event attachment - delete as event attachment
          await deleteAttachmentMutation.mutateAsync({
            eventId: existingEvent.id,
            attachmentId: file.id,
          })
        } else {
          // For new events without temp files, or other edge cases
          // Try temp file deletion as fallback
          await deleteTempFile(file.id)
        }

        // Update toast
        toast.success(`${file.name || 'File'} removed`, { id: deleteToastId })
      } else {
        // For files not yet uploaded or in other states, just remove from UI
        toast.success(`${file.name || 'File'} removed`)
      }

      // Remove from tracked temp file IDs if it was a newly uploaded temp file
      const fileIdToRemove = file.serverId || file.id
      if (fileIdToRemove && newTempFileIds.includes(fileIdToRemove)) {
        setNewTempFileIds((prev) => prev.filter((id) => id !== fileIdToRemove))
      }

      // Update form state to remove the file from the UI
      const currentAttachments = form.getValues('attachments') || []
      const updatedAttachments = currentAttachments.filter(
        (attachment) => attachment.id !== file.id
      )
      form.setValue('attachments', updatedAttachments, { shouldValidate: true })

      console.log('File deleted:', file)
    } catch (error) {
      console.error('Error deleting file:', error)
      toast.error(`Failed to delete ${file.name || 'file'}`)
    }
  }

  // Handle form field attachment updates
  const handleAttachmentsUpdate = (files: FilePreview[]) => {
    console.log('📎 handleAttachmentsUpdate called with files:', files)
    files.forEach((file, index) => {
      console.log(`  File ${index}:`, {
        id: file.id,
        name: file.name,
        status: file.status,
        hasFile: !!file.file,
        downloadUrl: file.downloadUrl,
      })
    })
    form.setValue('attachments', files, { shouldValidate: true })
  }

  // Initialize form with existing event data if available
  const form = useForm<EventStepOne>({
    defaultValues: existingEvent
      ? {
          name: existingEvent.name || '',
          open: existingEvent.open || false,
          invite_all_educators: defaultInviteAllEducators,
          invite_all_professionals: defaultInviteAllProfessionals,
          grade_levels: existingEvent.grade_levels || [],
          secondaryLanguages: existingEvent.secondary_languages || [],
          engagement_opportunities:
            existingEvent.engagement_opportunities || [],
          accomplish_statement: existingEvent.accomplish_statement || '',
          expectation_statement: existingEvent.expectation_statement || '',
          attachments:
            existingEvent.attachments?.map((a: any) => ({
              id: a.id,
              name: a.name,
              downloadUrl: a.downloadUrl,
              position: a.position,
              status: FileUploadStatus.Available, // Use the enum value expected by the component
              uploadURL: null,
            })) || [],
        }
      : ({
          name: '',
          open: false,
          invite_all_educators: false,
          invite_all_professionals: false,
          grade_levels: [],
          secondaryLanguages: [],
          engagement_opportunities: [],
          accomplish_statement: '',
          expectation_statement: '',
          attachments: [],
        } as EventStepOne),
    reValidateMode: 'onChange',
    resolver: yupResolver(schema as any) as any,
  })

  // Update form values when existingEvent changes or becomes available
  useEffect(() => {
    if (isEditMode && existingEvent) {
      // Ensure form values are updated if existingEvent changes after initial render
      form.setValue('name', existingEvent.name || '', { shouldValidate: true })
      form.setValue('open', existingEvent.open || false, {
        shouldValidate: true,
      })
      form.setValue('invite_all_educators', defaultInviteAllEducators, {
        shouldValidate: true,
      })
      form.setValue('invite_all_professionals', defaultInviteAllProfessionals, {
        shouldValidate: true,
      })
      form.setValue('grade_levels', existingEvent.grade_levels || [], {
        shouldValidate: true,
      })
      form.setValue(
        'secondaryLanguages',
        existingEvent.secondary_languages || [],
        { shouldValidate: true }
      )
      form.setValue(
        'engagement_opportunities',
        existingEvent.engagement_opportunities || [],
        { shouldValidate: true }
      )
      form.setValue(
        'accomplish_statement',
        existingEvent.accomplish_statement || '',
        { shouldValidate: true }
      )
      form.setValue(
        'expectation_statement',
        existingEvent.expectation_statement || '',
        { shouldValidate: true }
      )

      console.log('Existing event:', existingEvent)
      console.log('Existing event attachments:', existingEvent.attachments)
      if (existingEvent.attachments && existingEvent.attachments.length > 0) {
        const mappedAttachments = existingEvent.attachments.map((a: any) => ({
          id: a.id,
          name: a.name,
          downloadUrl: a.downloadUrl,
          position: a.position,
          status: FileUploadStatus.Available,
          uploadURL: null,
        }))
        form.setValue('attachments', mappedAttachments, {
          shouldValidate: true,
        })
        console.log('Set attachments in form:', mappedAttachments)
      }
    }
  }, [
    existingEvent,
    isEditMode,
    form,
    defaultInviteAllEducators,
    defaultInviteAllProfessionals,
  ])

  const handleSubmitForm = (
    redirect: string = '/app/events/{id}/two'
  ) => async (values: EventStepOne) => {
    // First, handle any pending file uploads
    let processedAttachments = values.attachments || []

    // Skip file upload step - files are already uploaded by AttachmentList component
    // Only process files that are truly pending (not already uploaded by AttachmentList)
    const actuallyPendingUploads = processedAttachments.filter(
      (attachment: FilePreview) =>
        attachment.file && attachment.status === FileUploadStatus.Pending
    )

    // Block submission if uploads are still pending
    if (actuallyPendingUploads.length > 0) {
      toast.error('Please wait for uploads to complete before saving.')
      return
    }

    console.log('Pending uploads:', processedAttachments)

    // Collect the IDs of all temporary files included in the form
    const tempFileIds: string[] = []

    // Only collect temp file IDs for new events, not edit mode
    if (!isEditMode) {
      // Use the tracked newTempFileIds which contains the IDs of files uploaded in this session
      tempFileIds.push(...newTempFileIds)
      console.log('Using newTempFileIds for event creation:', newTempFileIds)
    }

    if (
      canSelectOpenAudience &&
      values.open &&
      !values.invite_all_educators &&
      !values.invite_all_professionals
    ) {
      toast.error('Select at least one open event audience.')
      return
    }

    const audienceData = canSelectOpenAudience
      ? {
          invite_all_educators: values.open
            ? Boolean(values.invite_all_educators)
            : false,
          invite_all_professionals: values.open
            ? Boolean(values.invite_all_professionals)
            : false,
        }
      : {}

    // Prepare form data
    const formData = {
      name: values.name,
      open: values.open,
      ...audienceData,
      accomplish_statement: values.accomplish_statement,
      expectation_statement: values.expectation_statement,
      grade_levels: values.grade_levels ?? [],
      secondary_languages: values.secondaryLanguages ?? [],
      engagement_opportunities: values.engagement_opportunities ?? [],
      created_by: isEditMode ? existingEvent.created_by : user?.userId,
      organized_by: user?.userId,
      status: isEditMode ? existingEvent.status : 'DRAFT',
    }

    try {
      let response

      if (isEditMode && existingEvent) {
        // Update existing event using the updateEventMutation with temp file IDs
        response = await updateEventMutation.mutateAsync({
          id: existingEvent.id,
          ...formData,
          // Include newly uploaded temp file IDs for attachment
          temp_file_ids: newTempFileIds.length > 0 ? newTempFileIds : undefined,
        })

        // Clear the tracked temp file IDs since they're now attached
        if (newTempFileIds.length > 0) {
          setNewTempFileIds([])
          toast.success(
            `Event updated with ${newTempFileIds.length} file(s) attached`
          )
        } else {
          toast.success('Event updated successfully')
        }
      } else {
        // Create new event
        if (tempFileIds.length > 0) {
          // Create event with temporary file IDs
          response = await createEventWithTempFiles(formData, tempFileIds)

          toast.success('Event created with attached files')

          // If the response has redirect_url property, use that as the target redirect
          if (response && response.redirect_url) {
            navigate(response.redirect_url)
            return // Exit early since we're handling navigation directly
          }
        } else {
          // No temp files, use regular mutation
          response = await createEventMutation.mutateAsync({
            ...formData,
            status: 'DRAFT',
            organized_by: user?.userId || '',
          })

          // If redirecting to dashboard, show success toast
          if (redirect === '/app/dashboard') {
            toast.success('Event draft saved successfully')
          } else {
            toast.success('Event details saved successfully')
          }
        }

        // Clear the tracked temp file IDs after successful creation
        setNewTempFileIds([])
      }

      // Handle dynamic redirect with event ID
      const eventId = isEditMode ? existingEvent.id : response.id
      const targetRedirect = redirect.includes('{id}')
        ? redirect.replace('{id}', eventId)
        : redirect

      navigate(targetRedirect)
    } catch (error) {
      console.error('Error saving event:', error)
      toast.error(
        `There was an error ${isEditMode ? 'updating' : 'creating'} the event`
      )
    }
  }

  const onSaveDraft = async () => {
    await form.handleSubmit(handleSubmitForm('/app/dashboard'))() // Call the regular submit handler
  }

  const languages = ['Spanish', 'French', 'Latin', 'German', 'Arabic', 'Other']
  const gradeLevels = ['K-2', '3-5', '6-8', '9-10', '11-12', 'Other']
  const professionalExpectations = [
    'Share career journey and answer questions',
    'Promote internship opportunities',
    'Provide content/subject consultation with a class',
    'Support students with a classroom project by asking questions and linking current industry standards to the project',
    'Consult with a teacher on designing a real-world classroom project',
    'Become a member of a CTE Advisory Committee',
    'Participate in an exhibit or school fair',
    'Participate as a judge at an event (e.g. science fair)',
  ]

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmitForm())}
        className={clsx(isLoading && 'opacity-50')}
      >
        {!isEditMode && (
          <div className="flex flex-col mb-4 pb-4 border-b border-contentareaborder">
            <FormLabel>Existing Materials</FormLabel>
            <p className="font-body text-sm mt-1">
              Upload any flyers or documents you would like to share about this
              event.
            </p>
            <FormField
              control={form.control}
              name="attachments"
              render={({ field }) => (
                <FormItem className="w-full mt-4">
                  <FormControl>
                    <div>
                      {isUploadError && (
                        <div className="mb-2 text-red-500 text-sm">
                          Upload error:{' '}
                          {uploadError instanceof Error
                            ? uploadError.message
                            : 'Unknown error'}
                        </div>
                      )}
                      <AttachmentList
                        accept={[
                          'application/pdf',
                          'application/msword',
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          'image/png',
                          'image/jpeg',
                          'image/jpg',
                        ]}
                        previews={field.value || []}
                        onUpdate={handleAttachmentsUpdate}
                        onDelete={handleFileDelete}
                        onUpload={handleFileUpload}
                        onError={handleFileError}
                        getEndpointsForFilenames={getSignedUrls}
                        maxFiles={5}
                        label={
                          isUploading
                            ? 'Uploading files, please wait...'
                            : 'Drag & drop files here or click to upload (PDF, DOC, PNG, JPG)'
                        }
                      />
                      {isUploading && (
                        <div className="mt-2">
                          <p className="text-sm font-body">
                            Upload in progress...
                          </p>
                          <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1">
                            <div
                              className="bg-primary h-2.5 rounded-full"
                              style={{
                                width: '100%',
                                animation:
                                  'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                              }}
                            ></div>
                          </div>
                        </div>
                      )}

                      {/* Show analyze button when files are present (only in create mode) */}
                      {field.value && field.value.length > 0 && (
                        <div className="mt-3 flex flex-col">
                          <Button
                            type="button"
                            variant="default"
                            className="text-sm mx-auto"
                            disabled={isAnalyzing || isUploading}
                            onClick={() => {
                              // Get the first attachment id from the list
                              const attachments = field.value || []
                              console.log(
                                'Analyze button clicked - attachments:',
                                attachments
                              )

                              if (!attachments.length) {
                                toast.error('Please upload a document first')
                                return
                              }

                              // Debug: Log each attachment's status
                              attachments.forEach(
                                (a: FilePreview, index: number) => {
                                  console.log(`Attachment ${index}:`, {
                                    id: a.id,
                                    name: a.name,
                                    status: a.status,
                                    hasFile: !!a.file,
                                    downloadUrl: a.downloadUrl,
                                  })
                                }
                              )

                              // Find attachments that are available (uploaded successfully)
                              // More comprehensive check for uploaded files
                              const availableAttachments = attachments.filter(
                                (a: FilePreview) => {
                                  // Check multiple conditions for "available" files:
                                  // 1. Status is Available
                                  // 2. Has an ID and no file object (uploaded)
                                  // 3. Has a download URL (indicates successful upload)
                                  const isAvailable =
                                    a.status === FileUploadStatus.Available
                                  const isUploaded = a.id && !a.file
                                  const hasDownloadUrl = !!a.downloadUrl

                                  console.log(
                                    `File ${a.name} availability check:`,
                                    {
                                      isAvailable,
                                      isUploaded,
                                      hasDownloadUrl,
                                      finalResult:
                                        isAvailable ||
                                        isUploaded ||
                                        hasDownloadUrl,
                                    }
                                  )

                                  return (
                                    isAvailable || isUploaded || hasDownloadUrl
                                  )
                                }
                              )

                              console.log(
                                'Available attachments for analysis:',
                                availableAttachments
                              )

                              if (!availableAttachments.length) {
                                // Check if there are any files currently uploading
                                const uploadingFiles = attachments.filter(
                                  (a: FilePreview) =>
                                    a.status === FileUploadStatus.Pending
                                )
                                console.log('Uploading files:', uploadingFiles)

                                if (uploadingFiles.length > 0) {
                                  toast.error(
                                    'Please wait for uploads to complete. Files are still uploading.'
                                  )
                                } else {
                                  toast.error('Please upload a document first')
                                }
                                return
                              }

                              // Set analyzing state and show loading toast
                              setIsAnalyzing(true)
                              toast.loading('Analyzing your document...', {
                                id: 'analyze-toast',
                              })

                              // Call the analysis API
                              const attachmentIds = availableAttachments.map(
                                (a: FilePreview) => a.id
                              )
                              console.log(
                                'Calling analysis API with attachment IDs:',
                                attachmentIds
                              )

                              // We're not setting createEvent=true here since we want to preview the
                              // data first and let the user decide if they want to create the event
                              tempAnalysisMutation.mutate({
                                attachmentId: attachmentIds, // Using array format for the updated API
                                createEvent: true, // analyze only, don't create event yet
                              })
                            }}
                          >
                            {isAnalyzing ? (
                              <>
                                <Indicators.Spinner
                                  size="sm"
                                  className="mr-2"
                                />
                                Filling in form...
                              </>
                            ) : isUploading ? (
                              <>
                                <Indicators.Spinner
                                  size="sm"
                                  className="mr-2"
                                />
                                Waiting for upload...
                              </>
                            ) : (
                              <>
                                <span className="mr-2">✨</span> Fill out form
                                using my document
                              </>
                            )}
                          </Button>
                          <p className="text-xs text-center mt-1 text-body/60">
                            We'll read your document and fill in the form for
                            you
                          </p>

                          {/* Show a button to create event directly after analysis */}
                          {analyzedData && (
                            <div className="mt-3">
                              <Button
                                type="button"
                                variant="outline"
                                className="text-sm mx-auto"
                                onClick={() => {
                                  // Show loading toast
                                  const createToastId = toast.loading(
                                    'Creating event...'
                                  )

                                  // Call the API directly with createEvent=true to create the event in one step
                                  tempAnalysisMutation.mutate(
                                    {
                                      attachmentId: [analyzedData.attachmentId], // Using array format for the updated API
                                      createEvent: true, // create the event directly
                                    },
                                    {
                                      onSuccess: (data) => {
                                        if (
                                          data.success &&
                                          data.created_event
                                        ) {
                                          toast.success(
                                            'Event created! Redirecting...',
                                            { id: createToastId }
                                          )
                                          // Navigate to edit the event that was just created
                                          navigate(
                                            `/app/events/${data.created_event.id}/edit`
                                          )
                                        } else {
                                          toast.error(
                                            'Could not create event',
                                            { id: createToastId }
                                          )
                                          // Fall back to the old method if direct creation fails
                                          handleCreateFromAnalysis(
                                            analyzedData.formValues,
                                            analyzedData.attachmentId
                                          )
                                        }
                                      },
                                      onError: (error) => {
                                        toast.error(
                                          `Error creating event: ${error.message}`,
                                          { id: createToastId }
                                        )
                                        // Fall back to the old method if direct creation fails
                                        handleCreateFromAnalysis(
                                          analyzedData.formValues,
                                          analyzedData.attachmentId
                                        )
                                      },
                                    }
                                  )
                                }}
                              >
                                Create event and continue to next step
                              </Button>
                              <p className="text-xs text-center mt-1 text-body/60">
                                Skip filling in more details and move to the
                                next step
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        <FormLabel className="mt-8">Event Name</FormLabel>
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="hidden">Event Name</FormLabel>
              <p className="text-body font-body text-sm">
                Capture {isEducator ? "a professional's" : "a teacher's"}{' '}
                attention with a brief, descriptive title. This is the first
                thing they will see!
                <br />
                <br />
                Ex: "Be the Spark: Help 7th Graders See the Real-World Power of
                Science!"
              </p>
              <FormControl>
                <Input placeholder="Event Name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="open"
          render={({ field }) => (
            <div className="mt-4">
              <FormItem>
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="text-sm ml-2 text-body text-body/50">
                  {canSelectOpenAudience
                    ? 'This is an open event'
                    : 'This event is open to everyone'}
                </FormLabel>
              </FormItem>
            </div>
          )}
        />
        {canSelectOpenAudience && form.watch('open') ? (
          <div className="mt-4 rounded-md border border-contentareaborder p-4">
            <FormLabel className="text-sm font-semibold">
              Open event audience
            </FormLabel>
            <div className="mt-3 flex flex-col gap-3">
              <FormField
                control={form.control}
                name="invite_all_educators"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Checkbox
                        checked={Boolean(field.value)}
                        onCheckedChange={(checked) =>
                          field.onChange(Boolean(checked))
                        }
                      />
                    </FormControl>
                    <FormLabel className="text-sm ml-2 text-body text-body/50">
                      All educators
                    </FormLabel>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="invite_all_professionals"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Checkbox
                        checked={Boolean(field.value)}
                        onCheckedChange={(checked) =>
                          field.onChange(Boolean(checked))
                        }
                      />
                    </FormControl>
                    <FormLabel className="text-sm ml-2 text-body text-body/50">
                      All professionals
                    </FormLabel>
                  </FormItem>
                )}
              />
            </div>
          </div>
        ) : null}

        <div className="flex flex-col">
          <FormLabel className="mt-8">
            What Are You Trying to Accomplish?
          </FormLabel>
          <p className="font-body text-sm mt-3">
            {isEducator
              ? 'Inspire students, reinforce classroom content, promote career awareness, build communication and inquiry skills and make learning relevant.'
              : "Tell us a bit about what you're trying to accomplish with this invitation."}
          </p>
          <p className="font-body text-sm text-body/70 mt-3 mb-2">
            {isEducator
              ? "Ex. We're looking for passionate professionals to visit our 7th grade physical science classroom at Rhodes Middle School, (Mesa USD) for an in-person OR virtual 30-minute guest session. Our students are eager to learn how science is used beyond the classroom—and you can help make that connection! During this visit, you'll share your career pathway, answer student questions, and explain how science plays a role in your work."
              : 'Ex. As a healthcare professional from Banner Hospital, I would like to visit a middle school classroom to share information about entry-level careers in the health industry. My goal is to help students learn about the variety of opportunities available and how they can start preparing for these jobs in the future.'}
          </p>
          <FormField
            control={form.control}
            name="accomplish_statement"
            render={({ field }) => (
              <FormItem className="w-full col-span-3">
                <FormLabel className="hidden">Description</FormLabel>
                <FormControl>
                  <Textarea {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex flex-col mt-8">
          <FormLabel>
            {isEducator
              ? "What's Expected of the Professional(s)?"
              : 'What Are You Offering to Educators?'}
          </FormLabel>
          <p className="font-body text-sm mt-1">
            First, let's look at it from a high level. Check all that apply:
          </p>
          <FormField
            control={form.control}
            name="engagement_opportunities"
            render={({ field: { value, onChange } }) => (
              <div className="mt-4">
                {professionalExpectations.map((expectation) => (
                  <FormItem
                    key={expectation}
                    className="flex items-start space-x-2 mt-2"
                  >
                    <FormControl>
                      <Checkbox
                        className="mt-1"
                        checked={value?.includes(expectation)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            onChange([...(value ?? []), expectation])
                          } else {
                            onChange(value?.filter((e) => e !== expectation))
                          }
                        }}
                      />
                    </FormControl>
                    <FormLabel className="text-sm ml-2 text-body flex items-center">
                      {expectation}{' '}
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-body/50 cursor-help ml-1">
                              ⓘ
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-sm bg-white border border-gray-200 p-3 rounded shadow-lg">
                            <div className="text-sm">
                              <div className="font-semibold mb-2">
                                {getEngagementTooltip(expectation)?.title}
                              </div>
                              <div>
                                {getEngagementTooltip(expectation)?.content}
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </FormLabel>
                  </FormItem>
                ))}
              </div>
            )}
          />

          <p className="font-body text-sm mt-6">
            {isEducator
              ? 'What would you say if you were sending an email to this professional or talking on the phone about your students, classroom or this event?'
              : 'Explain how you would introduce your session to a teacher.'}
          </p>
          <p className="font-body mt-1 text-body/70 text-sm mb-2">
            {isEducator
              ? "Ex. My students are curious, hands-on learners who really engage when they can make real-world connections. During this visit, you'd share a little about your career path, how you use science in your work, and answer student questions. It's a relaxed, conversational setting with the goal of sparking student interest and helping them imagine what their future might look like. My students will have questions prepared in advance (please see attached list of possible questions)."
              : 'Ex. My team and I would love to join your classroom, either in person or virtually, to discuss health careers. Our goal is to help students understand the variety of jobs available and how they can prepare for these opportunities early on. I think it would be a great way to learn about entry level careers in a hospital.'}
          </p>

          <FormField
            control={form.control}
            name="expectation_statement"
            render={({ field }) => (
              <FormItem className="w-full col-span-3">
                <FormLabel className="hidden">Expectations</FormLabel>
                <FormControl>
                  <Textarea {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex flex-col mt-8">
          <FormLabel>What Grade Level is this For?</FormLabel>
          <p className="font-body text-sm mt-1">Choose all that apply.</p>
          <FormField
            control={form.control}
            name="grade_levels"
            render={({ field: { value, onChange } }) => (
              <div className="grid grid-cols-8 gap-2 mt-4">
                {gradeLevels.map((level) => (
                  <FormItem key={level} className="col-span-3">
                    <FormControl>
                      <Checkbox
                        checked={value?.includes(level)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            onChange([...(value ?? []), level])
                          } else {
                            onChange(value?.filter((l) => l !== level))
                          }
                        }}
                      />
                    </FormControl>
                    <FormLabel className="text-sm ml-2 text-body">
                      {level}
                    </FormLabel>
                  </FormItem>
                ))}
              </div>
            )}
          />
        </div>

        <div className="flex flex-col mt-8">
          <FormLabel>Any Special Language Requirements?</FormLabel>
          <p className="font-body text-sm mt-1">
            {isEducator
              ? 'Want the professional to speak fluently in another language besides English?'
              : 'Can you speak fluently in another language besides English?'}{' '}
            Choose all that apply.
          </p>
          <FormField
            control={form.control}
            name="secondaryLanguages"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="hidden">Secondary Languages</FormLabel>
                <FormControl>
                  <CreatableSelect
                    isDisabled={form.formState.isSubmitting}
                    isMulti
                    isClearable={false}
                    placeholder="Select or create languages"
                    options={languages.map((language) => ({
                      value: language,
                      label: language,
                    }))}
                    value={
                      field.value?.map((language) => ({
                        value: language,
                        label: language,
                      })) || []
                    }
                    onChange={(selectedOptions) => {
                      field.onChange(
                        selectedOptions.map((option) => option.value)
                      )
                    }}
                    onCreateOption={(inputValue) => {
                      field.onChange([...(field.value || []), inputValue])
                    }}
                    classNames={{
                      control: () =>
                        'flex w-full rounded-md border border-input bg-background py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <p className="text-xs font-body text-body/70 md:mr-28 mt-1">
            When you start typing, we'll suggest options that are already in our
            system. You can also create new language options if needed.
          </p>
        </div>

        {/* Show attachments after languages section in edit mode */}
        {isEditMode && (
          <div className="flex flex-col mt-8 pb-4 border-b border-contentareaborder">
            <FormLabel>Event Materials</FormLabel>
            <p className="font-body text-sm mt-1">
              Manage any flyers or documents associated with this event.
            </p>
            <FormField
              control={form.control}
              name="attachments"
              render={({ field }) => (
                <FormItem className="w-full mt-4">
                  <FormControl>
                    <div>
                      {isUploadError && (
                        <div className="mb-2 text-red-500 text-sm">
                          Upload error:{' '}
                          {uploadError instanceof Error
                            ? uploadError.message
                            : 'Unknown error'}
                        </div>
                      )}
                      <AttachmentList
                        accept={[
                          'application/pdf',
                          'application/msword',
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          'image/png',
                          'image/jpeg',
                          'image/jpg',
                        ]}
                        previews={field.value || []}
                        onUpdate={handleAttachmentsUpdate}
                        onDelete={handleFileDelete}
                        onUpload={handleFileUpload}
                        onError={handleFileError}
                        getEndpointsForFilenames={getSignedUrls}
                        maxFiles={5}
                        label={
                          isUploading
                            ? 'Uploading files, please wait...'
                            : 'Drag & drop files here or click to upload (PDF, DOC, PNG, JPG)'
                        }
                      />
                      {isUploading && (
                        <div className="mt-2">
                          <p className="text-sm font-body">
                            Upload in progress...
                          </p>
                          <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1">
                            <div
                              className="bg-primary h-2.5 rounded-full"
                              style={{
                                width: '100%',
                                animation:
                                  'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                              }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        <div className="flex-col flex mt-12 w-full">
          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            variant="default"
            className="mx-auto uppercase"
          >
            {form.formState.isSubmitting ? (
              <Indicators.Spinner
                size="lg"
                className="mt-auto mr-2 mb-auto ml-auto"
              />
            ) : null}
            <span>{isEditMode ? 'Save Changes' : 'Next: Where & When >'}</span>
          </Button>
          {!isEditMode && (
            <Button
              type="button"
              variant="link"
              className="text-actionable underline text-sm"
              onClick={onSaveDraft}
            >
              <span>Save Draft and Finish Later</span>
            </Button>
          )}
        </div>
      </form>
    </Form>
  )
}
